import React, { useState, useMemo, useCallback } from "react";
import { Heading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { useParent } from "../../contexts/ParentContext";
import { Avatar } from "../../design/avatar";
import { useCase } from "../../contexts/CaseContext";
import { getInitials } from "../../utils/string";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import { Select } from "../../design/select";
import { XMarkIcon, FunnelIcon } from "@heroicons/react/20/solid";
import EmptyStateView from "../../components/EmptyStateView";
import ViewToggle, { ViewMode } from "../../components/ViewToggle";
import ParentGrid from "./components/ParentGrid";
import {
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  MARITAL_STATUS_LABELS,
  INCOME_LEVEL_LABELS,
  EDUCATION_LEVEL_LABELS,
  EMPLOYMENT_STATUS_LABELS,
  HOUSING_STATUS_LABELS,
} from "@visitproof/types";

const ParentListPage: React.FC = () => {
  const { cases } = useCase();
  const { parents, error, loading } = useParent();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [showFilters, setShowFilters] = useState(false);

  // Demographic filters
  const [raceFilter, setRaceFilter] = useState("");
  const [sexFilter, setSexFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [maritalStatusFilter, setMaritalStatusFilter] = useState("");
  const [incomeLevelFilter, setIncomeLevelFilter] = useState("");
  const [educationLevelFilter, setEducationLevelFilter] = useState("");
  const [employmentStatusFilter, setEmploymentStatusFilter] = useState("");
  const [housingStatusFilter, setHousingStatusFilter] = useState("");
  const [ruralAreaFilter, setRuralAreaFilter] = useState("");
  const [translatorNeededFilter, setTranslatorNeededFilter] = useState("");

  // Memoized filtered parents
  const filteredParents = useMemo(() => {
    return parents.filter((parent) => {
      // Text search
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${parent.firstName} ${parent.lastName}`.toLowerCase();
        const caseIdentifier = cases.find((c) => c.id === parent.caseId)?.identifier?.toLowerCase() || "";
        if (!fullName.includes(searchLower) && !caseIdentifier.includes(searchLower)) {
          return false;
        }
      }

      // Demographic filters
      if (raceFilter && parent.race !== raceFilter) return false;
      if (sexFilter && parent.sex !== sexFilter) return false;
      if (languageFilter && parent.primaryLanguage !== languageFilter) return false;
      if (maritalStatusFilter && parent.maritalStatus !== maritalStatusFilter) return false;
      if (incomeLevelFilter && parent.incomeLevel !== incomeLevelFilter) return false;
      if (educationLevelFilter && parent.educationLevel !== educationLevelFilter) return false;
      if (employmentStatusFilter && parent.employmentStatus !== employmentStatusFilter) return false;
      if (housingStatusFilter && parent.housingStatus !== housingStatusFilter) return false;
      if (ruralAreaFilter === "yes" && parent.isRuralArea !== true) return false;
      if (ruralAreaFilter === "no" && parent.isRuralArea !== false) return false;
      if (translatorNeededFilter === "yes" && parent.translatorNeeded !== true) return false;
      if (translatorNeededFilter === "no" && parent.translatorNeeded !== false) return false;

      return true;
    });
  }, [parents, searchTerm, cases, raceFilter, sexFilter, languageFilter, maritalStatusFilter, incomeLevelFilter, educationLevelFilter, employmentStatusFilter, housingStatusFilter, ruralAreaFilter, translatorNeededFilter]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const activeFilterCount = [raceFilter, sexFilter, languageFilter, maritalStatusFilter, incomeLevelFilter, educationLevelFilter, employmentStatusFilter, housingStatusFilter, ruralAreaFilter, translatorNeededFilter].filter(Boolean).length;

  const hasActiveFilters = Boolean(searchTerm.trim()) || activeFilterCount > 0;

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setRaceFilter("");
    setSexFilter("");
    setLanguageFilter("");
    setMaritalStatusFilter("");
    setIncomeLevelFilter("");
    setEducationLevelFilter("");
    setEmploymentStatusFilter("");
    setHousingStatusFilter("");
    setRuralAreaFilter("");
    setTranslatorNeededFilter("");
  }, []);

  if (loading.list) {
    return <LoadingView />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  const hasParents = parents.length > 0;
  const hasFilteredParents = filteredParents.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Parents</Heading>
          {hasParents && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {filteredParents.length} of {parents.length} parents
              {hasActiveFilters && " (filtered)"}
            </p>
          )}
        </div>
        {hasParents && (
          <ViewToggle view={viewMode} onViewChange={setViewMode} />
        )}
      </div>

      {/* Search & Filters */}
      {hasParents && (
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 sm:max-w-sm">
              <Input
                type="search"
                placeholder="Search parents..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                plain
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                <FunnelIcon className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {hasActiveFilters && (
                <Button
                  plain
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Clear filters
                </Button>
              )}
            </div>
          </div>

          {/* Demographic Filters */}
          {showFilters && (
            <div className="p-4 border rounded-lg border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Race / Ethnicity</label>
                  <Select value={raceFilter} onChange={(e) => setRaceFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(RACE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Sex</label>
                  <Select value={sexFilter} onChange={(e) => setSexFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(SEX_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Primary Language</label>
                  <Select value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(LANGUAGE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Marital Status</label>
                  <Select value={maritalStatusFilter} onChange={(e) => setMaritalStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(MARITAL_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Employment Status</label>
                  <Select value={employmentStatusFilter} onChange={(e) => setEmploymentStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(EMPLOYMENT_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Housing Status</label>
                  <Select value={housingStatusFilter} onChange={(e) => setHousingStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(HOUSING_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Income Level</label>
                  <Select value={incomeLevelFilter} onChange={(e) => setIncomeLevelFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(INCOME_LEVEL_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Education Level</label>
                  <Select value={educationLevelFilter} onChange={(e) => setEducationLevelFilter(e.target.value)}>
                    <option value="">All</option>
                    {Object.entries(EDUCATION_LEVEL_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Rural Area</label>
                  <Select value={ruralAreaFilter} onChange={(e) => setRuralAreaFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-zinc-500 dark:text-zinc-400">Translator Needed</label>
                  <Select value={translatorNeededFilter} onChange={(e) => setTranslatorNeededFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {!hasParents ? (
        <EmptyStateView title="No Parents Found" message="Please create a case first" />
      ) : !hasFilteredParents ? (
        <div className="mt-8 text-center">
          <Heading>No parents match your search</Heading>
          <p className="mt-2 text-zinc-500 dark:text-zinc-400">
            Try adjusting your search terms or{" "}
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              clear all filters
            </button>
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <ParentGrid parents={filteredParents} cases={cases} />
      ) : (
        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Case</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredParents.map((parent) => (
              <TableRow key={parent.id} href={`/parent/${parent.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={parent.avatarUrl}
                      className="size-6"
                      initials={getInitials(
                        `${parent.firstName} ${parent.lastName}`,
                      )}
                    />
                    <span>{`${parent.firstName} ${parent.lastName}`}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {cases.find((c) => c.id === parent.caseId)?.identifier ??
                    parent.caseId}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ParentListPage;
